import { useId, useMemo, useState, type FormEvent } from 'react'
import { Activity, Check, ChevronRight, Compass, MessageCircle, Send, Search, UserMinus, UserPlus, Users, X } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { StartScreen } from './StartScreen'
import { FriendActivity } from './FriendActivity'
import { displayName, useSocialStore } from '@/store/social'
import { describeError } from '@/lib/auth'
import { cx } from '@/lib/cx'
import type { PresenceStatus, Relationship } from '@/types/api'
import { STATUS_LABEL, activityOf, isOnline } from '@/lib/presence'
import { useCustomStatus, usePresenceStore } from '@/store/presence'
import { Avatar } from '../Avatar'
import { avatarUrl } from '@/lib/avatar'
import chatStyles from '../ChatView.module.css'
import styles from './FriendsPage.module.css'
import { MobileBack } from '@/components/MobileBack'
import { StyledName } from '../StyledName'
import { userContextHandler } from '@/store/contextMenu'

type Tab = 'online' | 'all' | 'pending' | 'blocked' | 'add'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

export function FriendsPage() {
  // без друзей и серверов показываем стартовый экран;
  // ждём READY, чтобы не мигнуть им у того, чьи серверы ещё не пришли
  const ready = useAppStore((s) => s.gateway === 'ready')
  const noGuilds = useGuildStore((s) => Object.keys(s.guilds).length === 0)
  const hasFriends = useSocialStore(s => Object.values(s.relationships).some(r => r.type === 1))
  if (ready && noGuilds && !hasFriends) return <StartScreen />
  return <FriendsList />
}

function FriendsList() {
  const [tab, setTab] = useState<Tab>('online')
  const [query, setQuery] = useState('')
  const [activityOpen, setActivityOpen] = useState(false)
  const relationships = useSocialStore((s) => s.relationships)
  const presences = useSocialStore((s) => s.presences)
  const pendingIn = useMemo(() => Object.values(relationships).filter((r) => r.type === 3).length, [relationships])

  const rows = useMemo(() => {
    const all = Object.values(relationships)
    const q = query.trim().toLowerCase()
    const match = (r: Relationship) => !q || r.user.username.includes(q) || displayName(r.user).toLowerCase().includes(q)
    const sorted = (list: Relationship[]) => list.filter(match).sort((a, b) => displayName(a.user).localeCompare(displayName(b.user), 'ru'))
    switch (tab) {
      case 'online':
        return sorted(all.filter((r) => r.type === 1 && isOnline(presences[r.id])))
      case 'all':
        return sorted(all.filter((r) => r.type === 1))
      case 'pending':
        return sorted(all.filter((r) => r.type === 3 || r.type === 4))
      case 'blocked':
        return sorted(all.filter((r) => r.type === 2))
      default:
        return []
    }
  }, [relationships, presences, tab, query])

  const label: Record<Exclude<Tab, 'add'>, string> = { online: 'В сети', all: 'Все друзья', pending: 'Ожидание', blocked: 'Заблокированные' }

  return (
    <main className={chatStyles.chat} aria-label="Друзья">
      <header className={cx(chatStyles.header, styles.header)}>
        <MobileBack label="К списку бесед" />
        <Users size={24} className={chatStyles.hash} />
        <h1 className={chatStyles.title}>Друзья</h1>
        <span className={styles.divider} />
        <nav className={styles.tabs} aria-label="Разделы">
          {(['online', 'all', 'pending', 'blocked'] as const).map((t) => (
            <button key={t} type="button" className={cx(styles.tab, tab === t && styles.tabActive)} onClick={() => { setTab(t); setActivityOpen(false) }} aria-current={tab === t ? 'page' : undefined}>
              {label[t]}
              {t === 'pending' && pendingIn ? <span className={styles.tabBadge}>{pendingIn}</span> : null}
            </button>
          ))}
          <button type="button" className={cx(styles.tab, styles.tabAdd, tab === 'add' && styles.tabAddActive)} onClick={() => { setTab('add'); setActivityOpen(false) }} aria-label="Добавить в друзья" data-tooltip="Добавить в друзья">
            <UserPlus size={20} className={styles.tabAddIcon} aria-hidden="true" />
            <span className={styles.tabAddText}>Добавить в друзья</span>
          </button>
        </nav>
        <button type="button" className={cx(chatStyles.iconButton, styles.activityToggle)} aria-label="Активность друзей" data-tooltip="Активность друзей" aria-pressed={activityOpen} onClick={() => setActivityOpen(open => !open)}><Activity size={20} /></button>
      </header>

      <div className={styles.content} data-activity={activityOpen ? '' : undefined}><div className={styles.list}>
      {tab === 'add' ? (
        <AddFriend discovery />
      ) : (
        <div className={cx(styles.body, 'scroller')}>
          <label className={styles.search}>
            <input className={styles.searchInput} placeholder="Поиск" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Поиск по друзьям" />
            <Search size={18} className={styles.searchIcon} />
          </label>
          <h2 className={styles.sectionTitle}>{label[tab]} — {rows.length}</h2>
          {rows.length === 0 ? <Empty tab={tab} /> : rows.map((r) => <FriendRow key={r.id} rel={r} status={presences[r.id] ?? 'offline'} />)}
        </div>
      )}
      </div><FriendActivity /></div>
    </main>
  )
}

function FriendRow({ rel, status }: { rel: Relationship; status: PresenceStatus }) {
  const setView = useAppStore((s) => s.setView)
  const { openDM, acceptFriend, removeRelationship } = useSocialStore()
  const [busy, setBusy] = useState(false)
  const name = displayName(rel.user)
  const online = isOnline(status)
  // у друга в сети — его свой статус, иначе статус словами
  const custom = useCustomStatus(rel.type === 1 && online ? rel.id : undefined)
  const activities = usePresenceStore(s => s.activities[rel.id])
  const activity = rel.type === 1 && online ? activityOf(activities) : null
  const subtitle = rel.type === 1 ? custom || activity?.label || STATUS_LABEL[online ? status : 'offline'] : rel.type === 3 ? 'Входящая заявка в друзья' : rel.type === 4 ? 'Исходящая заявка в друзья' : 'Заблокирован'

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true)
    try {
      await fn()
    } finally {
      setBusy(false)
    }
  }

  const openChat = run(async () => setView({ kind: 'home', channelId: await openDM(rel.id) }))

  return (
    <div className={styles.row} onContextMenu={userContextHandler(rel.id, { user: rel.user })}>
      {/* вся строка друга открывает беседу, как в Discord; кнопки справа лежат поверх и работают сами */}
      {rel.type === 1 ? <button type="button" className={styles.rowOpen} aria-label={`Открыть беседу с ${name}`} disabled={busy} onClick={openChat} /> : null}
      <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(rel.user.id)} size={32} status={rel.type === 1 ? (online ? status : 'offline') : undefined} className={styles.rowAvatar} src={avatarUrl(rel.user.id, rel.user.avatar)} decoration={rel.user.avatar_decoration_data} />
      <div className={styles.rowText}>
        <span className={styles.rowName}><StyledName name={name} style={rel.user.display_name_styles} /> <span className={styles.rowUsername}>@{rel.user.username}</span></span>
        <span className={styles.rowSub} data-tooltip={custom || undefined}>{subtitle}</span>
      </div>
      <div className={styles.rowActions}>
        {rel.type === 1 ? (
          <>
            <button type="button" data-tooltip="Написать" aria-label={`Написать ${name}`} disabled={busy} onClick={openChat}><MessageCircle size={20} /></button>
            <button type="button" data-tooltip="Удалить из друзей" aria-label={`Удалить ${name} из друзей`} className={styles.dangerAction} disabled={busy} onClick={run(() => removeRelationship(rel.id))}><UserMinus size={20} /></button>
          </>
        ) : rel.type === 3 ? (
          <>
            <button type="button" data-tooltip="Принять" aria-label={`Принять заявку от ${name}`} className={styles.okAction} disabled={busy} onClick={run(() => acceptFriend(rel.id))}><Check size={20} /></button>
            <button type="button" data-tooltip="Отклонить" aria-label={`Отклонить заявку от ${name}`} className={styles.dangerAction} disabled={busy} onClick={run(() => removeRelationship(rel.id))}><X size={20} /></button>
          </>
        ) : rel.type === 4 ? (
          <button type="button" data-tooltip="Отменить заявку" aria-label={`Отменить заявку к ${name}`} className={styles.dangerAction} disabled={busy} onClick={run(() => removeRelationship(rel.id))}><X size={20} /></button>
        ) : (
          <button type="button" data-tooltip="Разблокировать" aria-label={`Разблокировать ${name}`} className={styles.dangerAction} disabled={busy} onClick={run(() => removeRelationship(rel.id))}><X size={20} /></button>
        )}
      </div>
    </div>
  )
}

function Empty({ tab }: { tab: Exclude<Tab, 'add'> }) {
  const text: Record<typeof tab, string> = {
    online: 'Никого из друзей нет в сети. Загляните позже.',
    all: 'Пока нет друзей. Добавьте кого-нибудь по имени пользователя.',
    pending: 'Нет ожидающих заявок.',
    blocked: 'Никто не заблокирован.',
  }
  return <p className={styles.empty}>{text[tab]}</p>
}

export function AddFriend({ discovery = false }: { discovery?: boolean }) {
  const setView = useAppStore((s) => s.setView)
  const showContent = useAppStore((s) => s.showContent)
  const helpId = useId()
  const resultId = useId()
  const requestFriend = useSocialStore((s) => s.requestFriend)
  const [username, setUsername] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const name = username.trim().replace(/^@/, '')
    if (!name || busy) return
    setBusy(true)
    setResult(null)
    try {
      await requestFriend(name)
      setResult({ ok: true, text: `Заявка отправлена @${name}.` })
      setUsername('')
    } catch (err) {
      const d = describeError(err)
      setResult({ ok: false, text: d.fields.username ?? d.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={cx(styles.body, styles.addPage, 'scroller')}>
      <form className={styles.add} onSubmit={submit}>
        <div className={styles.addHeading}>
          <div>
            <h2 className={styles.addTitle}>Добавить в друзья</h2>
            <p className={styles.addText} id={helpId}>Добавляйте друзей по имени пользователя в Кличате.</p>
          </div>
          <img className={styles.addLogo} src="/icon-512.png" alt="" aria-hidden="true" />
        </div>
        <div className={cx(styles.addField, result && (result.ok ? styles.addOk : styles.addError))}>
          <div className={styles.addInputWrap}>
            <span className={styles.addAt} aria-hidden="true">@</span>
            <input
              className={styles.addInput}
              placeholder="Введите имя пользователя"
              value={username}
              onChange={(e) => { setUsername(e.target.value.replace(/^\s*@/, '').toLowerCase()); setResult(null) }}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="none"
              autoFocus
              readOnly={busy}
              aria-label="Имя пользователя"
              aria-describedby={result ? `${helpId} ${resultId}` : helpId}
              aria-invalid={result && !result.ok ? true : undefined}
            />
          </div>
          <button type="submit" className={styles.addButton} disabled={busy || !username.trim()}>
            <Send size={16} aria-hidden="true" />
            {busy ? 'Отправляем…' : 'Отправить заявку'}
          </button>
        </div>
        {result ? <p id={resultId} className={cx(styles.addResult, result.ok ? styles.addResultOk : styles.addResultError)} role={result.ok ? 'status' : 'alert'}>{result.text}</p> : null}
      </form>
      {discovery ? (
        <section className={styles.discover} aria-labelledby={`${helpId}-discover`}>
          <h2 className={styles.discoverTitle} id={`${helpId}-discover`}>Знакомьтесь на серверах</h2>
          <p className={styles.discoverText}>Откройте каталог и выберите сообщество по интересам.</p>
          <button type="button" className={styles.discoverButton} onClick={() => { setView({ kind: 'discovery' }); showContent() }}>
            <span className={styles.discoverIcon}><Compass size={26} aria-hidden="true" /></span>
            <span className={styles.discoverLabel}>
              <span>Исследовать серверы</span>
              <span className={styles.discoverSub}>Открытые сообщества Кличата</span>
            </span>
            <ChevronRight size={20} className={styles.discoverArrow} aria-hidden="true" />
          </button>
        </section>
      ) : null}
    </div>
  )
}
